import React, { useState, useCallback, useEffect } from 'react';
import TopNav from '../layouts/TopNav';
import { ToastContainer, toast } from 'react-toastify';
import ReLoginModal from '../components/ReLoginModal';
import { CircularProgress, LinearProgress } from '@mui/material';
import Swal from 'sweetalert2';
import CustomerModal from '../components/Modals/selectModal/CustomerSelectModal';
import LicenseSelectModal from '../components/Modals/selectModal/LicenceSelectModal';
import ProductSelectModal from '../components/Modals/selectModal/ProductSelectModal';
import { useNavigate } from 'react-router-dom';
import api from '../util/axios';

interface AllocationFields {
    license: string;
    // customerId: string;
    destination: string;
    programId: string;
    truckNumber: string;
    quantity: string;
    product: { id: string; name: string; selling: number; };
    exDepotPrice: string;
}

interface Customer {
    _id: string;
    businessName: string;
    customerRef: string;
}

interface Product {
    _id: string;
    name: string;
    description: string;
    unitOfMeasure: string;
    category: string;
    selling: number;
    quantity: string;
    customerId: string;
}

const CreateAllocation: React.FC = () => {
    const navigate = useNavigate();
    const [reLogin, setReLogin] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isFetchingLicense, setIsFetchingLicense] = useState(false);
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<string>('');
    const [licenseNumberInput, setLicenseNumberInput] = useState<string>(''); // Store licenseNumber for input
    const [fields, setFields] = useState<AllocationFields>({
        license: '',
        // customerId: '',
        destination: '',
        programId: '',
        truckNumber: '',
        quantity: '',
        product: { id: '', name: '', selling: 0 },
        exDepotPrice: '',
    });
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<{ id: string; name: string; selling: number } | null>(null);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'license') {
            setLicenseNumberInput(value);
        } else {
            setFields((prev) => ({ ...prev, [name]: value }));
        }
    }, []);

    const handleSelectCustomer = useCallback((customer: Customer) => {
        setSelectedCustomer(customer);
        setFields((prev) => ({ ...prev, customerId: customer._id }));
        setIsCustomerModalOpen(false);
    }, []);

    const handleSelectProduct = useCallback((product: { id: string; name: string; selling: number }) => {
        setSelectedProduct(product);
        setFields((prev) => ({ ...prev, product }));
        setIsProductModalOpen(false);
    }, []);

    const handleSelectLicense = useCallback(
        (licenseData: {
            _id: string;
            licenseNumber: string;
            licenseOwner: string;
            destination: string;
            documentUrl?: string;
            programId?: string;
            truckNumber?: string;
            quantity?: string;
            product?: { id: string; name: string; selling: number };
            exDepotPrice?: string;
            issuedDate?: string;
            expiryDate?: string;
        }) => {
            setFields({
                license: licenseData._id,
                // customerId: licenseData.customerId,
                destination: licenseData.destination,
                programId: licenseData.programId || '',
                truckNumber: licenseData.truckNumber || '',
                quantity: licenseData.quantity || '',
                product: licenseData.product || { id: '', name: '', selling: 0 },
                exDepotPrice: licenseData.exDepotPrice || '',
            });
            setLicenseNumberInput(licenseData.licenseNumber);
            // setSelectedCustomer({ _id: licenseData.customerId, businessName: licenseData.businessName });
            setSelectedProduct(licenseData.product || null);
            setDocumentUrl(licenseData.documentUrl || '');
            setIsLicenseModalOpen(false);
        },
        []
    );

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            const token = localStorage.getItem('token');
            if (!token) {
                 
                setReLogin(true);
                return;
            }

            if (!fields.product.id) {
                toast.error('Please select a product.');
                return;
            }

            setIsLoading(true);
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            };

            try {
                const payload = {
                    license: fields.license,
                    programId: selectedCustomer?.customerRef,
                    customer: selectedCustomer?._id,
                    truckNumber: fields.truckNumber,
                    quantity: parseFloat(fields.quantity) || 0,
                    product: {
                        id: fields.product.id,
                        name: fields.product.name,
                        selling: fields.product.selling,
                    },
                    exDepotPrice: fields.product.selling || 0,
                    destination: fields.destination,
                    status: 'open',
                };
                // console.log('Submitting payload:', payload);
                const response = await api.post(
                    '/daily-allocation',
                    payload,
                    { headers }
                );
                Swal.fire({
                    title: 'Success',
                    text: response.data.message || 'Allocation created successfully!',
                    icon: 'success',
                    confirmButtonColor: 'green',
                    confirmButtonText: 'OK',
                });

                toast.success('Allocation created successfully!');
                setFields({
                    license: '',
                    // customerId: '',
                    destination: '',
                    programId: '',
                    truckNumber: '',
                    quantity: '',
                    product: { id: '', name: '', selling: 0 },
                    exDepotPrice: '',
                });
                setLicenseNumberInput('');
                setSelectedCustomer(null);
                setSelectedProduct(null);
                setDocumentUrl('');
            } catch (error: any) {
                const errorMessage =
                    error.response?.data?.message || error.message || 'Failed to create allocation.';
                if (errorMessage.includes('jwt expired') || errorMessage.includes('jwt malformed')) {
                    setReLogin(true);
                    toast.error('Session expired, please log in again.');
                } else {
                    Swal.fire({
                        title: 'Error',
                        text: errorMessage,
                        icon: 'error',
                        confirmButtonColor: 'red',
                        confirmButtonText: 'OK',
                    });
                }
            } finally {
                setIsLoading(false);
            }
        },
        [fields]
    );

    return (
        <div className="registerLicenseBody h-screen">
            <ToastContainer />
            <TopNav title="Create Daily Allocation" />
            <div className="registerLicenseContainer flex justify-between my-8 mx-8 bg-white shadow-md h-5/6">
                <div className="licenseForm w-6/12 p-8">
                    {isFetchingLicense && (
                        <div className="mb-4">
                            <LinearProgress />
                        </div>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div className="license">
                            <div className="licenseInput my-2 flex items-center">
                                <label>License: </label>
                                <input
                                    type="text"
                                    name="license"
                                    value={licenseNumberInput}
                                    onChange={handleChange}
                                    required
                                    disabled={isFetchingLicense}
                                />
                                <button
                                    type="button"
                                    className="ml-2 outline-none cursor-pointer bg-gray-200 px-2 py-1 rounded"
                                    onClick={() => setIsLicenseModalOpen(true)}
                                    disabled={isFetchingLicense}
                                >
                                    ...
                                </button>
                            </div>
                            <div className="licenseInput my-2 flex items-center">
                                <label>License Customer: </label>
                                <input
                                    type="text"
                                    value={selectedCustomer?.businessName || ''}
                                    readOnly
                                    required
                                />
                                <button
                                    type="button"
                                    className="ml-2 outline-none cursor-pointer bg-gray-200 px-2 py-1 rounded"
                                    onClick={() => setIsCustomerModalOpen(true)}
                                    disabled={isFetchingLicense}
                                >
                                    ...
                                </button>
                            </div>
                            {selectedCustomer && (
                                <p className="block text-sm text-gray-500 mt-1">ID: {selectedCustomer._id}</p>
                            )}
                            <div className="licenseInput my-2 flex items-center">
                                <label>Program ID: </label>
                                <input
                                    type="text"
                                    name="programId"
                                    value={selectedCustomer?.customerRef}
                                    onChange={handleChange}
                                    readOnly
                                    required
                                />
                            </div>
                            <div className="licenseInput my-2 flex items-center">
                                <label>Truck Number: </label>
                                <input
                                    type="text"
                                    name="truckNumber"
                                    value={fields.truckNumber}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="licenseInput my-2 flex items-center">
                                <label>Product: </label>
                                <input
                                    type="text"
                                    value={selectedProduct?.name || ''}
                                    readOnly
                                    required
                                />
                                <button
                                    type="button"
                                    className="ml-2 outline-none cursor-pointer bg-gray-200 px-2 py-1 rounded"
                                    onClick={() => setIsProductModalOpen(true)}
                                    disabled={isFetchingLicense}
                                >
                                    ...
                                </button>
                            </div>
                            <div className="licenseInput my-2 flex items-center">
                                <label>Quantity: </label>
                                <input
                                    type="number"
                                    name="quantity"
                                    value={fields.quantity}
                                    onChange={handleChange}
                                    required
                                    min="0"
                                />
                            </div>
                            <div className="licenseInput my-2 flex items-center">
                                <label>Ex Depot Price: </label>
                                <input
                                    type="text"
                                    name="exDepotPrice"
                                    value={selectedProduct?.selling || ''}
                                    onChange={handleChange}
                                    required
                                    readOnly
                                // min="0"
                                />
                            </div>
                            <div className="licenseInput my-2 flex items-center">
                                <label>Destination: </label>
                                <input
                                    type="text"
                                    name="destination"
                                    value={fields.destination}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="licenseActions">
                            <div className="licenseFormButtons flex gap-8 mt-4 w-full">
                                <button type="submit" className="saveBtn" disabled={isLoading || isFetchingLicense}>
                                    {isLoading ? (
                                        <CircularProgress style={{ color: '#fff', width: '25px', height: '25px' }} />
                                    ) : (
                                        'Save'
                                    )}
                                </button>
                                <button
                                    onClick={() => {
                                        navigate(-1);
                                    }}
                                    type="button"
                                    className="formCancelBtn"
                                    disabled={isClosing}
                                >
                                    {isClosing ? <CircularProgress size={30} /> : 'Cancel'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
                <div className="licenseDocument w-6/12 flex justify-center">
                    {documentUrl ? (
                        <img src={documentUrl} alt="license document" />
                    ) : (
                        <div className="tempDoc mt-16">
                            <p className='bg-blue-400 p-4 rounded-lg'>No document available</p>
                        </div>
                    )}
                </div>
            </div>
            {reLogin && <ReLoginModal />}
            <LicenseSelectModal
                open={isLicenseModalOpen}
                onClose={() => setIsLicenseModalOpen(false)}
                onSelect={handleSelectLicense}
            />
            <CustomerModal
                open={isCustomerModalOpen}
                onClose={() => setIsCustomerModalOpen(false)}
                onSelect={handleSelectCustomer}
            />
            <ProductSelectModal
                open={isProductModalOpen}
                onClose={() => setIsProductModalOpen(false)}
                onSelect={handleSelectProduct}
                customerID={selectedCustomer?._id || ''} // Pass customer ID if available
            />
        </div>
    );
};

export default CreateAllocation;